<script setup>
// One money-transfer rail: an icon chip on each end, an ink line between them that
// draws in on scroll, and a pulse riding it in hard mechanical steps — the same
// signature motion as HeroFlow, reused here (without its floating "via" aside, since
// both rows on the geldstroom page are real transfers, not an aside) so the two
// "gescheiden geldstromen" read as one visual language instead of a slogan next to a
// diagram that doesn't match it.
//
// The amounts don't animate, on purpose: see HeroFlow's header comment. A figure
// scrubbing through € 1.727,27 on its way to € 4.750,00 is a plausible-looking
// collection shortfall, and no amount of motion is worth that. The arrival is carried
// by the pulse and by one word behind the figure ("onderweg" → "binnen"), which is
// scrubbed like everything else and can't be misread as money.
// `variant="muted"` shrinks and quiets the whole rail — used for the fee row, which
// should read as the small exception next to the bold, primary contributions row.
//
// Unlike HeroFlow's mount-once timeline, this one is scrubbed to scroll position
// (see the scrollTrigger below): it has no fixed moment to play once since it
// lives inside a snap-page the user may enter, leave, and re-enter by scrolling
// either direction, so tying it to the scrollbar itself is what makes it feel
// physically connected rather than a canned clip that happens to autoplay nearby.
import { ref, computed, onMounted, onUnmounted } from "vue";
import { formatMoney } from "@/lib/format";
import { gsap, ScrollTrigger, useMotion, reducedMotion, collapseRail } from "@/lib/motion";

const props = defineProps({
  eyebrow: { type: String, required: true },
  fromIcon: { type: [Object, Function], required: true },
  fromLabel: { type: String, required: true },
  amountCents: { type: Number, required: true },
  toIcon: { type: [Object, Function], required: true },
  toLabel: { type: String, required: true },
  variant: { type: String, default: "primary" }, // "primary" | "muted"
  startDelay: { type: Number, default: 0 },
  landedSuffix: { type: String, default: "binnen" },
  pendingSuffix: { type: String, default: "onderweg" },
});

const bold = computed(() => props.variant === "primary");

const amountLabel = computed(() => formatMoney(props.amountCents));
const arrived = ref(reducedMotion);

const root = ref(null);
const railLine = ref(null);
const railArrow = ref(null);
const pulseDot = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    const railTip = collapseRail(railLine.value);
    gsap.set(railArrow.value, { opacity: 0 });
    gsap.set(root.value, { opacity: 0, y: 14 });
    // Own the dot's vertical centring through GSAP (yPercent) rather than the
    // -translate-y-1/2 utility class: GSAP writes transform as one inline value, so
    // animating x/left below would otherwise clobber a CSS-driven translateY.
    gsap.set(pulseDot.value, { yPercent: -50 });

    // Travel distance in px (measured once against the track, mirroring the
    // 2%-to-94% span the old `left` tween covered) so the pulse animates via a
    // GPU-compositable transform instead of forcing a layout recalc every scrub tick.
    const travel = pulseDot.value.parentElement.offsetWidth * 0.92;

    // Scrubbed to scroll position (not once: true) rather than fired once on
    // entry — the draw and the pulse track the scrollbar directly, so scrolling
    // down advances the transfer and scrolling back up unwinds it smoothly instead
    // of the animation firing once and freezing on arrival. Everything still hangs
    // off the "travel" label (rather than gsap's "<"/">" shortcuts) since several
    // parallel pieces — the arrowhead, the pulse, the arrival — all start at the
    // same instant the rail finishes drawing.
    const tl = gsap
      .timeline({ paused: true })
      .to({}, { duration: props.startDelay })
      .to(root.value, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" })
      .to(railLine.value, { attr: { x2: railTip }, duration: 0.5, ease: "power2.inOut" }, "-=0.1")
      .addLabel("travel")
      .to(railArrow.value, { opacity: 1, duration: 0.15 }, "travel")
      .to(pulseDot.value, { opacity: 1, duration: 0.05 }, "travel")
      .to(pulseDot.value, { x: travel, duration: 0.85, ease: "steps(14)" }, "travel")
      .to(pulseDot.value, { opacity: 0, duration: 0.1 }, "travel+=0.73");

    // Where the pulse lands. Recomputed from the timeline's own progress on every
    // scrub tick rather than fired as a one-shot callback, because a scrubbed
    // timeline is just as likely to run backwards: scroll up and the word has to
    // un-arrive with the pulse it belongs to.
    const arrivesAt = tl.labels.travel + 0.73;

    ScrollTrigger.create({
      trigger: root.value,
      start: "top 88%",
      end: "top 40%",
      scrub: 0.4,
      animation: tl,
      onUpdate: (self) => {
        const next = self.progress * tl.duration() >= arrivesAt;
        if (next !== arrived.value) arrived.value = next;
      },
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <div ref="root" class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-5">
    <p
      class="font-mono font-semibold uppercase tracking-[0.1em] sm:w-32 sm:shrink-0"
      :class="bold ? 'text-[0.6875rem] text-primary' : 'text-[0.625rem] text-muted-foreground'"
    >
      {{ eyebrow }}
    </p>

    <!-- Instrument row, then readout row — same split as HeroFlow, and for the same
         reason: with the labels and figures inside the grid they eat the middle
         column, and on a phone the rail is left with barely a stub to draw. -->
    <div class="flex-1">
      <div class="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-4">
        <span
          class="flex items-center justify-center border-2 border-foreground"
          :class="bold ? 'size-9 bg-secondary text-primary sm:size-11' : 'size-8 bg-card text-muted-foreground sm:size-9'"
        >
          <component :is="fromIcon" :class="bold ? 'size-4 sm:size-5' : 'size-4'" />
        </span>

        <div class="relative h-5">
          <svg class="h-4 w-full" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
            <line
              ref="railLine"
              x1="1"
              y1="10"
              x2="92"
              y2="10"
              stroke="currentColor"
              :stroke-width="bold ? 2 : 1.5"
              vector-effect="non-scaling-stroke"
              :class="bold ? 'text-foreground' : 'text-foreground/35'"
            />
            <path
              ref="railArrow"
              d="M88 4 L97 10 L88 16"
              fill="none"
              stroke="currentColor"
              :stroke-width="bold ? 2 : 1.5"
              vector-effect="non-scaling-stroke"
              :class="bold ? 'text-foreground' : 'text-foreground/35'"
            />
          </svg>
          <span
            ref="pulseDot"
            class="absolute left-[2%] top-1/2 border border-foreground opacity-0"
            :class="bold ? 'size-2.5 bg-primary' : 'size-2 bg-muted-foreground'"
            aria-hidden="true"
          />
        </div>

        <span
          class="flex items-center justify-center border-2 border-foreground"
          :class="bold ? 'size-9 bg-primary text-primary-foreground sm:size-11' : 'size-8 bg-card text-muted-foreground sm:size-9'"
        >
          <component :is="toIcon" :class="bold ? 'size-4 sm:size-5' : 'size-4'" />
        </span>
      </div>

      <div class="mt-1.5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{{ fromLabel }}</p>
          <p class="font-mono font-semibold tabular-nums text-foreground" :class="bold ? 'text-base sm:text-xl' : 'text-sm'">{{ amountLabel }}</p>
        </div>
        <div class="min-w-0 text-right">
          <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{{ toLabel }}</p>
          <!-- whitespace-nowrap: the state word belongs on the amount's line. Wrapping
               it costs a line per rail, and this figure lives on the tightest page on
               the site at phone widths. -->
          <p
            class="whitespace-nowrap font-mono font-semibold tabular-nums"
            :class="bold ? 'text-base text-primary sm:text-xl' : 'text-sm text-muted-foreground'"
          >
            {{ amountLabel }}
            <span class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{{ arrived ? landedSuffix : pendingSuffix }}</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
